import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MessageCircle, Send, Loader2, Plus, Search, Users, User, Phone, Video, Paperclip, Info, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminMessages() {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [dmConvos, setDmConvos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [chatType, setChatType] = useState('all');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/messages/all').catch(() => ({ data: [] })),
      api.get('/communication/direct-messages').catch(() => ({ data: [] })),
      api.get('/communication/groups').catch(() => ({ data: [] })),
      api.get('/drivers').catch(() => ({ data: [] })),
      api.get('/customers').catch(() => ({ data: [] })),
    ]).then(([loadMsgs, dms, grps, drivers, customers]) => {
      setConversations(loadMsgs.data || []);
      setDmConvos(dms.data || []);
      setGroups(grps.data || []);
      setAllUsers([...(drivers.data || []), ...(customers.data || [])]);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const openLoadChat = (convo) => {
    setActiveChat({ type: 'load', id: convo.load_id, name: convo.cargo, subtitle: `${convo.pickup_name} → ${convo.delivery_name}` });
    api.get(`/messages/${convo.load_id}`)
      .then(r => setMessages(r.data)).catch(console.error);
  };

  const openDmChat = (dm) => {
    const otherId = dm.sender_id === user?.user_id ? dm.recipient_id : dm.sender_id;
    const otherName = dm.sender_id === user?.user_id ? dm.recipient_name : dm.sender_name;
    setActiveChat({ type: 'dm', id: otherId, name: otherName || otherId?.slice(-6), subtitle: 'Direct Message' });
    api.get(`/communication/direct-messages/${otherId}`)
      .then(r => setMessages(r.data || [])).catch(() => setMessages([]));
  };

  const openGroupChat = (group) => {
    setActiveChat({ type: 'group', id: group.group_id, name: group.name, subtitle: `${group.member_count || '?'} members`, groupData: group });
    api.get(`/communication/groups/${group.group_id}/messages`)
      .then(r => setMessages(r.data || [])).catch(() => setMessages([]));
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !activeChat) return;
    setSending(true);
    try {
      if (activeChat.type === 'load') {
        const res = await api.post('/messages', { load_id: activeChat.id, content: input.trim() });
        setMessages(prev => [...prev, res.data]);
      } else if (activeChat.type === 'dm') {
        await api.post('/communication/direct-messages', { recipient_id: activeChat.id, content: input.trim(), message_type: 'text' });
        const r = await api.get(`/communication/direct-messages/${activeChat.id}`);
        setMessages(r.data || []);
      } else if (activeChat.type === 'group') {
        await api.post(`/communication/groups/${activeChat.id}/messages`, { content: input.trim(), message_type: 'text' });
        const r = await api.get(`/communication/groups/${activeChat.id}/messages`);
        setMessages(r.data || []);
      }
      setInput('');
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  // Build unified conversation list
  const allChats = [];
  conversations.forEach(c => allChats.push({ type: 'load', id: c.load_id, name: c.cargo || 'Load', preview: c.last_message?.content, time: c.last_message?.created_at, count: c.message_count, raw: c }));
  // Group DMs by unique partner
  const dmPartners = {};
  (dmConvos || []).forEach(dm => {
    const otherId = dm.sender_id === user?.user_id ? dm.recipient_id : dm.sender_id;
    if (!dmPartners[otherId]) dmPartners[otherId] = dm;
  });
  Object.values(dmPartners).forEach(dm => {
    const otherId = dm.sender_id === user?.user_id ? dm.recipient_id : dm.sender_id;
    const otherName = dm.sender_id === user?.user_id ? (dm.recipient_name || otherId?.slice(-6)) : (dm.sender_name || otherId?.slice(-6));
    allChats.push({ type: 'dm', id: otherId, name: otherName, preview: dm.content, time: dm.created_at, raw: dm });
  });
  groups.forEach(g => allChats.push({ type: 'group', id: g.group_id, name: g.name, preview: g.description, time: g.created_at, count: g.member_count, raw: g }));

  let filteredChats = allChats;
  if (chatType !== 'all') filteredChats = filteredChats.filter(c => c.type === chatType);
  if (search) { const q = search.toLowerCase(); filteredChats = filteredChats.filter(c => c.name?.toLowerCase().includes(q)); }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;

  return (
    <div className="flex border border-border rounded-xl overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
      {/* Left Panel: Conversations */}
      <div className="w-80 flex-shrink-0 border-r border-border flex flex-col bg-card">
        <div className="p-3 border-b border-border space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground">Messages</h2>
            <Button size="sm" className="h-7 bg-brand-accent text-brand-accent-foreground text-[10px] font-bold" onClick={() => setNewChatOpen(true)}>
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              className="w-full bg-muted border border-input text-foreground text-xs rounded-lg pl-8 pr-3 py-1.5" />
          </div>
          <div className="flex gap-1">
            {[{ id: 'all', label: 'All' }, { id: 'dm', label: 'Direct' }, { id: 'group', label: 'Groups' }, { id: 'load', label: 'Loads' }].map(t => (
              <button key={t.id} onClick={() => setChatType(t.id)}
                className={`text-[10px] font-bold px-2 py-1 rounded transition-colors flex-1 ${
                  chatType === t.id ? 'bg-brand-accent/10 text-brand-accent' : 'text-muted-foreground hover:text-foreground'
                }`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredChats.map((c, i) => (
            <div key={`${c.type}-${c.id}-${i}`}
              onClick={() => c.type === 'load' ? openLoadChat(c.raw) : c.type === 'dm' ? openDmChat(c.raw) : openGroupChat(c.raw)}
              className={`p-3 border-b border-border cursor-pointer transition-colors ${
                activeChat?.id === c.id ? 'bg-muted/50' : 'hover:bg-muted/30'
              }`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                  c.type === 'group' ? 'bg-brand-accent/20 text-foreground' : c.type === 'load' ? 'bg-brand-accent/20 text-brand-accent' : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {c.type === 'group' ? <Users className="w-4 h-4" /> : c.type === 'load' ? <MessageCircle className="w-4 h-4" /> : c.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-foreground font-medium truncate">{c.name}</p>
                    {c.time && <span className="text-[10px] text-muted-foreground ml-2 flex-shrink-0">{new Date(c.time).toLocaleDateString()}</span>}
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">{c.preview || 'No messages'}</p>
                </div>
                {c.count > 0 && <Badge className="text-[8px] bg-brand-accent/20 text-brand-accent h-4 px-1">{c.count}</Badge>}
              </div>
            </div>
          ))}
          {filteredChats.length === 0 && <p className="text-muted-foreground text-xs text-center py-8">No conversations</p>}
        </div>
      </div>

      {/* Right Panel: Chat */}
      {activeChat ? (
        <div className="flex-1 flex flex-col bg-background">
          {/* Chat Header */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                activeChat.type === 'group' ? 'bg-brand-accent/20 text-foreground' : 'bg-brand-accent/20 text-brand-accent'
              }`}>
                {activeChat.type === 'group' ? <Users className="w-4 h-4" /> : activeChat.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">{activeChat.name}</p>
                <p className="text-[10px] text-muted-foreground">{activeChat.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" title="Voice Call">
                <Phone className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" title="Video Call"
                onClick={() => { const room = `freightos-${Date.now()}`; toast.info(`Video call room: ${room}`); }}>
                <Video className="w-4 h-4" />
              </Button>
              {activeChat.type === 'group' && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" onClick={() => setGroupInfoOpen(true)}>
                  <Info className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {messages.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No messages yet. Start the conversation!</p>}
            {messages.map((m, i) => {
              const isMe = m.sender_id === user?.user_id;
              return (
                <div key={m.message_id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                    isMe ? 'bg-brand-accent text-brand-accent-foreground' : 'bg-muted text-foreground border border-input'
                  }`}>
                    {!isMe && <p className="text-[10px] font-bold opacity-60 mb-0.5">{m.sender_name}</p>}
                    {m.message_type === 'voice' ? (
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-6 bg-foreground/10 rounded-full" />
                        <span className="text-[10px]">{m.voice_duration_seconds}s</span>
                      </div>
                    ) : (
                      <p className="text-sm">{m.content}</p>
                    )}
                    <p className={`text-[10px] mt-1 ${isMe ? 'text-brand-accent-foreground/40' : 'text-muted-foreground'}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-border px-4 py-3 flex gap-2 shrink-0">
            <Button variant="ghost" size="sm" className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground shrink-0" title="Attach file">
              <Paperclip className="w-4 h-4" />
            </Button>
            <Input value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="bg-muted border-input text-foreground rounded-xl h-10 flex-1" />
            <Button onClick={handleSend} disabled={sending || !input.trim()}
              className="bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground rounded-xl h-10 px-4 shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-center">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Select a conversation or start a new one</p>
          </div>
        </div>
      )}

      {/* New Chat Dialog */}
      <NewChatDialog open={newChatOpen} onOpenChange={setNewChatOpen} token={token} users={allUsers} user={user}
        onCreated={(chat) => { setNewChatOpen(false); if (chat.type === 'dm') openDmChat(chat.raw); else if (chat.type === 'group') openGroupChat(chat.raw); }} />

      {/* Group Info Panel */}
      {activeChat?.type === 'group' && (
        <GroupInfoDialog open={groupInfoOpen} onOpenChange={setGroupInfoOpen} group={activeChat.groupData} token={token} users={allUsers} />
      )}
    </div>
  );
}

function NewChatDialog({ open, onOpenChange, token, users, user, onCreated }) {
  const [tab, setTab] = useState('dm');
  const [selectedUser, setSelectedUser] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleCreateDM = async () => {
    if (!selectedUser) return;
    onCreated({ type: 'dm', raw: { sender_id: user?.user_id, recipient_id: selectedUser, sender_name: user?.name, content: '' } });
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) { toast.error('Group name and members required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/communication/groups', {
        name: groupName.trim(), description: '', group_type: 'custom', member_ids: selectedMembers,
      });
      toast.success('Group created');
      onCreated({ type: 'group', raw: res.data });
    } catch { toast.error('Failed to create group'); }
    setSaving(false);
  };

  const toggleMember = (uid) => {
    setSelectedMembers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border text-foreground max-w-sm">
        <DialogHeader><DialogTitle>New Conversation</DialogTitle></DialogHeader>
        <div className="flex gap-1 mb-3">
          <button onClick={() => setTab('dm')} className={`flex-1 text-[10px] font-bold py-1.5 rounded-lg ${tab === 'dm' ? 'bg-brand-accent/10 text-brand-accent' : 'text-muted-foreground'}`}>
            <User className="w-3 h-3 inline mr-1" /> Direct Message
          </button>
          <button onClick={() => setTab('group')} className={`flex-1 text-[10px] font-bold py-1.5 rounded-lg ${tab === 'group' ? 'bg-brand-accent/10 text-brand-accent' : 'text-muted-foreground'}`}>
            <Users className="w-3 h-3 inline mr-1" /> Group Chat
          </button>
        </div>
        {tab === 'dm' ? (
          <div className="space-y-3">
            <div><Label className="text-muted-foreground text-xs">Select User</Label>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
                className="w-full mt-1 bg-muted border border-input text-foreground text-sm rounded-lg px-3 py-2">
                <option value="">Choose...</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role || u.email})</option>)}
              </select>
            </div>
            <Button onClick={handleCreateDM} disabled={!selectedUser} className="w-full bg-brand-accent text-brand-accent-foreground font-bold rounded-lg">Start Chat</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-muted-foreground text-xs">Group Name</Label>
              <Input value={groupName} onChange={e => setGroupName(e.target.value)} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="e.g. Dispatch Team" /></div>
            <div><Label className="text-muted-foreground text-xs">Members ({selectedMembers.length})</Label>
              <div className="max-h-40 overflow-y-auto mt-1 space-y-1">
                {users.map(u => (
                  <button key={u.user_id} onClick={() => toggleMember(u.user_id)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${
                      selectedMembers.includes(u.user_id) ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}>
                    <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-bold">{u.name?.charAt(0)}</div>
                    {u.name}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleCreateGroup} disabled={saving || !groupName.trim()} className="w-full bg-brand-accent text-brand-accent-foreground font-bold rounded-lg">
              {saving ? 'Creating...' : 'Create Group'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GroupInfoDialog({ open, onOpenChange, group, token, users }) {
  const [members, setMembers] = useState([]);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  useEffect(() => {
    if (open && group?.group_id) {
      api.get(`/communication/groups/${group.group_id}/members`)
        .then(r => setMembers(r.data || []))
        .catch(() => setMembers([]));
    }
  }, [open, group?.group_id, token]);

  const addMember = async (userId) => {
    try {
      await api.post(`/communication/groups/${group.group_id}/members`, { user_id: userId });
      toast.success('Member added');
      const r = await api.get(`/communication/groups/${group.group_id}/members`);
      setMembers(r.data || []);
    } catch { toast.error('Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border text-foreground max-w-sm">
        <DialogHeader><DialogTitle>{group?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">{group?.description || 'No description'}</p>
        <div className="flex items-center justify-between mt-3">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">MEMBERS ({members.length})</p>
          <Button size="sm" className="h-6 text-[10px] bg-brand-accent text-brand-accent-foreground" onClick={() => setAddMemberOpen(!addMemberOpen)}>
            <UserPlus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        {addMemberOpen && (
          <div className="max-h-32 overflow-y-auto space-y-1 border border-input rounded-lg p-2">
            {users.filter(u => !members.find(m => m.user_id === u.user_id)).map(u => (
              <button key={u.user_id} onClick={() => addMember(u.user_id)}
                className="w-full flex items-center gap-2 p-1.5 rounded text-left text-xs text-muted-foreground hover:text-foreground hover:bg-muted">
                <Plus className="w-3 h-3" /> {u.name}
              </button>
            ))}
          </div>
        )}
        <div className="space-y-1.5 mt-2">
          {members.map(m => (
            <div key={m.user_id} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
              <div className="w-7 h-7 rounded-full bg-brand-accent/20 flex items-center justify-center text-xs font-bold text-foreground">{m.name?.charAt(0) || '?'}</div>
              <div className="flex-1"><p className="text-xs text-foreground">{m.name || m.user_id}</p></div>
              <Badge className="text-[8px] bg-zinc-700 text-muted-foreground">{m.role}</Badge>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
